import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Card, MutationError, latestError } from "@csa/web-foundation";
import { useT } from "../i18n/index";
import { TcknField } from "./tckn-field";
import { useConfirmLink, useInvalidateProfile, useRedeem, useStartLink } from "./use-linking";
import type { LinkOutcome } from "../api/contracts";

const MAX_ATTEMPTS = 5;

// The linking ceremony — the account-activation moment (ADR 0005 §3.1). Path A
// (invitation token in the URL → redeem) is the pilot default; Path B (TCKN → OTP) is
// self-service. HARD INVARIANT preserved: no path links on TCKN alone — the possession
// factor (the invitation token delivered to the on-file contact, or the OTP) is
// load-bearing. Failures are UNIFORM (one "DOĞRULANAMADI", no cause distinction — the
// client mirrors the API's uniform DENIED); 5 attempts → time-boxed lockout; the
// advisor path is ALWAYS offered (and works during lockout).
export function Onboarding() {
  const { t } = useT();
  const [params] = useSearchParams();
  const token = params.get("token");
  const invalidateProfile = useInvalidateProfile();

  const start = useStartLink();
  const confirm = useConfirmLink();
  const redeem = useRedeem();

  const [tckn, setTckn] = useState("");
  const [ceremonyId, setCeremonyId] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [, setAttempts] = useState(0); // count is only read to trigger the lockout below
  const [locked, setLocked] = useState(false);
  const [failed, setFailed] = useState(false);
  const [linked, setLinked] = useState(false);

  const busy = start.isPending || confirm.isPending || redeem.isPending;

  // Branch ONLY on the uniform outcome — the client cannot tell DENIED apart by cause.
  function onOutcome(outcome: LinkOutcome) {
    if (outcome === "LINKED") {
      setLinked(true);
      return;
    }
    if (outcome === "LOCKED") {
      setLocked(true);
      return;
    }
    setFailed(true);
    setAttempts((a) => {
      const n = a + 1;
      if (n >= MAX_ATTEMPTS) setLocked(true);
      return n;
    });
  }

  function submitTckn() {
    if (tckn.length !== 11) return;
    setFailed(false);
    start.mutate(
      { tckn },
      { onSuccess: (ticket) => setCeremonyId(ticket.ceremonyId) }, // uniform: always a ticket
    );
  }
  function submitOtp() {
    if (!ceremonyId || otp.length !== 6) return;
    setFailed(false);
    confirm.mutate({ ceremonyId, otp }, { onSuccess: (r) => onOutcome(r.outcome) });
  }
  function submitRedeem() {
    if (!token) return;
    setFailed(false);
    redeem.mutate({ token }, { onSuccess: (r) => onOutcome(r.outcome) });
  }

  const AdvisorPath = (
    <p className="muted" data-testid="advisor-path">
      {t("onb.advisor")}
    </p>
  );
  /* DENIED is a UNIFORM outcome the server returns on purpose — it must not say
     whether the identity exists. Every OTHER failure (429 rate-limit, 503, an
     unreachable network) is a different fact and used to render nothing at all,
     so a locked-out customer tapped "Doğrula" against a screen that never
     answered. The two are kept apart deliberately: DeniedNote keeps its
     non-committal copy, this reports the transport/server failure. */
  const CeremonyError = (
    <MutationError
      error={latestError(start, confirm, redeem)}
      fallback={t("onb.ceremonyFailed")}
      offline={t("onb.offline")}
    />
  );

  const DeniedNote = failed ? (
    <p role="alert" className="form-error" data-testid="denied">
      {t("onb.denied")}
    </p>
  ) : null;

  // ── Success → landing ──
  if (linked) {
    // GAP-AUDIT-2 B2 — burada eskiden bir "Passkey kur" / "Atla" ikilisi vardı ve İKİSİ DE
    // aynı şeyi yapıyordu (`invalidateProfile()`); kod tabanında hiçbir WebAuthn çağrısı yok.
    // Yani kullanıcı passkey kurduğunu sanıyor, hiçbir şey kurulmuyordu — bir GÜVENLİK
    // afordansının sessizce yalan söylemesi. Var olmayan bir seremoniyi taklit etmektense
    // ekranı dürüst bir onaya indirdik. Gerçek WebAuthn realm-side bir iş (CR1); geldiğinde
    // buraya kendi ceremony'siyle ayrı bir adım olarak eklenir — sahte bir buton olarak değil.
    return (
      <main className="center">
        <Card data-testid="linked-done">
          <h1>{t("onb.done.title")}</h1>
          <p className="muted">{t("onb.done.body")}</p>
          <div className="rec-decision-actions">
            <Button variant="primary" onClick={() => void invalidateProfile()}>
              {t("onb.done.continue")}
            </Button>
          </div>
        </Card>
      </main>
    );
  }

  // ── Time-boxed lockout — the advisor-invitation path stays open ──
  if (locked) {
    return (
      <main className="center">
        <Card data-testid="locked">
          <h1>{t("locked.title")}</h1>
          <p className="muted">{t("locked.body")}</p>
          {AdvisorPath}
        </Card>
      </main>
    );
  }

  // ── Path A — invitation redeem (works during lockout) ──
  if (token) {
    return (
      <main className="center">
        <Card data-testid="onboarding-invite">
          <h1>{t("onb.inviteTitle")}</h1>
          <p className="muted">{t("onb.inviteBody")}</p>
          {DeniedNote}
          {CeremonyError}
          <Button variant="primary" data-testid="redeem" disabled={busy} onClick={submitRedeem}>
            {t("onb.activate")}
          </Button>
          {AdvisorPath}
        </Card>
      </main>
    );
  }

  // ── Path B — self-service linking (TCKN → on-file OTP) ──
  return (
    <main className="center">
      <Card data-testid="onboarding-self">
        <h1>{t("onb.title")}</h1>
        {ceremonyId === null ? (
          <>
            <p className="muted">{t("onb.selfHint")}</p>
            <TcknField value={tckn} onChange={setTckn} />
            {DeniedNote}
            {CeremonyError}
            <Button
              variant="primary"
              data-testid="tckn-submit"
              disabled={busy || tckn.length !== 11}
              onClick={submitTckn}
            >
              {t("onb.continue")}
            </Button>
          </>
        ) : (
          <>
            <p className="muted" data-testid="otp-sent">
              {t("onb.otpSent")}
            </p>
            <label className="ui-field">
              {t("onb.otpLabel")}
              <input
                className="ui-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                aria-label={t("onb.otpLabel")}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
            </label>
            {DeniedNote}
            {CeremonyError}
            <Button
              variant="primary"
              data-testid="otp-submit"
              disabled={busy || otp.length !== 6}
              onClick={submitOtp}
            >
              {t("onb.verify")}
            </Button>
          </>
        )}
        {AdvisorPath}
      </Card>
    </main>
  );
}
